import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Check, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { getStoredRef } from "../lib/referral";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Subscribe() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast({ title: "Hold on", description: "Please enter a valid email address." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim(), source: "signup-page", ref: getStoredRef() }, { timeout: 15000 });
      setDone(true);
      trackEvent("sign_up", { method: "signup-page" });
      setEmail("");
    } catch (err) {
      toast({ title: "Oops", description: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16 bg-gradient-to-b from-slate-50 to-cyan-50 dark:from-slate-950 dark:to-slate-900">
      <div className="w-full max-w-lg">
        <Link to="/" data-testid="signup-back-home" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-cyan-300 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Sharkbowl Games
        </Link>

        <div className="relative overflow-hidden rounded-[2rem] ocean-gradient px-7 py-12 sm:px-12 sm:py-14 text-center shadow-[0_30px_80px_-20px_rgba(14,127,168,0.55)] border-4 border-white/30">
          <div className="absolute -top-12 -right-12 w-56 h-56 bg-white/20 rounded-full blur-2xl animate-blob" />
          <div className="absolute -bottom-16 -left-10 w-64 h-64 bg-cyan-200/20 rounded-full blur-2xl animate-blob" />

          <div className="relative z-10">
            {done ? (
              <div data-testid="signup-success">
                <span className="inline-flex w-16 h-16 rounded-full bg-white/25 items-center justify-center mb-5">
                  <Check className="w-8 h-8 text-white" />
                </span>
                <h1 className="font-display font-800 text-3xl sm:text-4xl text-white">You're on the list! 🦈</h1>
                <p className="text-white/85 mt-3 text-base">
                  Please check your inbox to confirm your subscription. Keep an eye out for news, new games and the occasional bit of Sharkbowl mischief.
                </p>
                <Link to="/">
                  <Button data-testid="signup-home-btn" className="mt-7 h-12 px-8 rounded-full bg-white text-teal-700 hover:bg-cyan-50 font-semibold">
                    Explore our games
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-white/20 text-white mb-5">
                  <Sparkles className="w-3.5 h-3.5" /> Sharkbowl Mailing List
                </span>
                <h1 className="font-display font-800 text-3xl sm:text-4xl text-white leading-tight">
                  Join the crew
                </h1>
                <p className="text-white/85 mt-3 text-base max-w-md mx-auto">
                  Be first to hear about new game releases, demos and behind-the-scenes updates from the bowl. No spam, unsubscribe anytime.
                </p>

                <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-600/70" />
                    <Input
                      type="email"
                      data-testid="signup-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-13 pl-12 rounded-full bg-white border-0 text-slate-800 h-12"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="signup-submit-btn"
                    disabled={loading}
                    className="h-12 px-7 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold whitespace-nowrap"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign me up"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
